import ActionButton from "Components/atoms/buttons/action-button"
import SyncButton from "Components/molecule/sync-button"
import AreYouSureModal from "Components/organisms/are-you-sure-modal"
import SearchOpportunityDashboardForm from "Components/organisms/forms/search-opportunity-dashboard-form"
import OpportunityDashboardTemplate from "Components/templates/business-development/opportunity-dashboard"
import useManual from "hooks/useManual"
import useFiscalYearOptions from "hooks/useFiscalYearOptions"
import { SyncLogTypes } from "models/sync-log"
import {
    OPPORTUNITY_DASHBOARD_TABS,
    OpportunityDashboardFilters,
    OpportunityDashboardView,
    filtersKey,
    viewFromTab,
} from "models/opportunity-dashboard"
import { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { createSelector } from "reselect"
import { OpportunitiesSliceState, setShowSyncModal } from "slices/business-development/opportunities/reducer"
import { syncOpportunitydata } from "slices/business-development/opportunities/thunk"
import useSyncProcess from "hooks/useSyncProcess"
import {
    OpportunityDashboardSliceState,
    emptyFilters,
    setOpportunityDashboardFilters,
    setOpportunityDashboardLoading,
    setOpportunityDashboardSearchFields,
    setOpportunityDashboardView,
} from "slices/business-development/opportunity-dashboard/reducer"
import {
    getOpportunityDashboardSearchFields,
    getOpportunityFunnel,
    getOpportunityList,
    getOpportunityOutcome,
} from "slices/business-development/opportunity-dashboard/thunk"
import { SyncLogSliceState } from "slices/sync-log/reducer"
import { getMostRecentSyncLog } from "slices/thunk"
import { HelpIcon } from "Components/atoms/icons"

const selectOpportunities = createSelector(
    (state: any) => state.Opportunities,
    ({ showSyncModal, isSyncingData }: OpportunitiesSliceState) => ({
        showSyncModal, isSyncingData
    })
)

const selectSyncLog = createSelector(
    (state: any) => state.SyncLog,
    ({ syncLogs }: SyncLogSliceState) => ({
        syncLogs
    })
)

const selectDashboard = createSelector(
    (state: any) => state.OpportunityDashboard,
    ({ filters, searchFields, views, loadedFor, isLoading }: OpportunityDashboardSliceState) => ({
        filters, searchFields, views, loadedFor, isLoading
    })
)

const loadView = (view: OpportunityDashboardView, filters: OpportunityDashboardFilters) => {
    switch (view) {
        case "funnel": return getOpportunityFunnel(filters)
        case "opportunities": return getOpportunityList(filters)
        default: return getOpportunityOutcome(view, filters)
    }
}

const OpportunitiesPage = () => {
    document.title = "Opportunities | IbexIQ";

    const dispatch: any = useDispatch();
    const { handleOpenManual } = useManual("business-development/opportunities");
    const { showSyncModal } = useSelector(selectOpportunities);
    const { syncLogs } = useSelector(selectSyncLog)
    const { filters, searchFields, views, loadedFor, isLoading } = useSelector(selectDashboard)
    const { options: fiscalYearOptions, defaultYear, isLoading: fiscalYearsLoading } = useFiscalYearOptions(filters.fiscalYear)

    const [activeTab, setActiveTab] = useState<string>(OPPORTUNITY_DASHBOARD_TABS.funnel)
    const activeView = viewFromTab(activeTab)
    const key = filtersKey(filters)

    // Only the most recent request may update the page (filters can change while one is in flight).
    const requestId = useRef(0)

    const initializePage = async () => {
        if (!syncLogs[SyncLogTypes.OPPORTUNITY_ACUMATICA])
            await dispatch(getMostRecentSyncLog(SyncLogTypes.OPPORTUNITY_ACUMATICA))

        if (!searchFields) {
            const fields = await getOpportunityDashboardSearchFields()
            if (fields) {
                // First visit: MPP and PP (the only divisions offered) start selected.
                dispatch(setOpportunityDashboardFilters({ ...filters, divisionIds: fields.divisions.map(d => d.id) }))
                dispatch(setOpportunityDashboardSearchFields(fields))
            }
        }
    }

    useEffect(() => {
        initializePage()
    }, [])

    // Load the active tab the first time it is opened, and again whenever the filters change.
    // Waits for the filter options so the first load already has the default divisions.
    useEffect(() => {
        if (!searchFields) return
        const id = ++requestId.current
        if (loadedFor[activeView] === key) {
            // Already loaded: cancel any request still in flight for another tab or filter.
            if (isLoading) dispatch(setOpportunityDashboardLoading(false))
            return
        }

        const load = async () => {
            dispatch(setOpportunityDashboardLoading(true))
            const data = await loadView(activeView, filters)
            if (id !== requestId.current) return
            dispatch(setOpportunityDashboardView({ view: activeView, key, data }))
            dispatch(setOpportunityDashboardLoading(false))
        }
        load()
    }, [activeView, key, searchFields])

    const handleChangeFilters = (next: OpportunityDashboardFilters) => {
        dispatch(setOpportunityDashboardFilters(next))
    }

    const handleClearFilters = () => {
        dispatch(setOpportunityDashboardFilters({
            ...emptyFilters(fiscalYearsLoading ? filters.fiscalYear : defaultYear),
            divisionIds: (searchFields?.divisions ?? []).map(d => d.id),
        }))
    }

    // This just opens the are you sure modal.
    const handleOpenAreYouSureModal = () => {
        dispatch(setShowSyncModal(true))
    }

    const handleAreYouSureDecline = async () => {
        dispatch(setShowSyncModal(false))
    }

    const onModalClose = () => {
        dispatch(setShowSyncModal(false))
    }

    // After the sync, reload the tab on screen
    const { isSyncing: isSyncingData, startSync } = useSyncProcess({
        module: SyncLogTypes.OPPORTUNITY_ACUMATICA,
        start: syncOpportunitydata,
        onFinished: async () => {
            dispatch(setOpportunityDashboardLoading(true))
            const data = await loadView(activeView, filters)
            dispatch(setOpportunityDashboardView({ view: activeView, key, data }))
            dispatch(setOpportunityDashboardLoading(false))
        },
    })

    const handleSyncData = async () => {
        dispatch(setShowSyncModal(false))
        await startSync()
    }

    return <OpportunityDashboardTemplate
        filters={<SearchOpportunityDashboardForm
            fiscalYears={fiscalYearOptions}
            searchFields={searchFields}
            filters={filters}
            onChange={handleChangeFilters}
            onClear={handleClearFilters}
        />}
        views={views}
        isLoading={isLoading}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        actions={
            <>
                <SyncButton
                    id="projectsSyncBtn"
                    isSyncing={isSyncingData}
                    syncLog={syncLogs[SyncLogTypes.OPPORTUNITY_ACUMATICA]}
                    onClick={handleOpenAreYouSureModal}
                />
                <ActionButton
                    onClick={handleOpenManual}
                >
                    Help <HelpIcon />
                </ActionButton>
            </>
        }
        modal={<AreYouSureModal
            isOpen={showSyncModal}
            onModalClose={onModalClose}
            modalTitle={"Are you sure you want to sync?"}
            handleAreYouSureAccept={handleSyncData}
            handleAreYouSureDecline={handleAreYouSureDecline}
            areYouSureacceptText={isSyncingData ? 'Syncing...' : "Sync Data"}
            areYouSuredeclineText={"Cancel"} />}
    />
}

export default OpportunitiesPage
